import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material'
import { Vehicle } from '@otw/models/inventory'
import { useAdminTransporter } from 'hooks/admin/useAdminTransporter'
import { useLogistics } from 'hooks/logistics/useLogistics'
import VehicleSelector from './VehicleSelector'

interface TruckSelectorDialogProps {
  open: boolean
  onClose: () => void
}

const TruckSelectorDialog: React.FC<TruckSelectorDialogProps> = ({ open, onClose }) => {
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([])

  const { getVehiclePackageInfo } = useAdminTransporter()
  const { addTransportTruck, isAddingTransportTruck, transportTrucks } = useLogistics()

  const vehicleIdsInTransportFleet = React.useMemo(
    () => new Set(transportTrucks.map((t) => t.vehicle_id)),
    [transportTrucks]
  )

  const resetForm = () => {
    setSelectedVehicles([])
  }

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  const handleVehicleSelectionChange = (vehicles: Vehicle[]) => {
    if (vehicles.length <= 1) {
      setSelectedVehicles(vehicles)
    } else {
      setSelectedVehicles([vehicles[vehicles.length - 1]])
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleAddTruck = async () => {
    const vehicle = selectedVehicles[0]
    const vin = vehicle?.vin?.trim()
    if (!vehicle?.id || !vin) return
    try {
      await addTransportTruck({ vehicleId: vehicle.id, vin })
      handleClose()
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 560 } }}
    >
      <DialogTitle>Add Transport Truck</DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select one truck from inventory to add to the transport fleet
          </Typography>
          <VehicleSelector
            selectedVehicles={selectedVehicles}
            onVehicleSelectionChange={handleVehicleSelectionChange}
            getVehiclePackageInfo={getVehiclePackageInfo}
            maxHeight={480}
            disabledVehicleIds={Array.from(vehicleIdsInTransportFleet)}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="secondary" disabled={isAddingTransportTruck}>
          Cancel
        </Button>
        <Button
          onClick={handleAddTruck}
          variant="contained"
          color="primary"
          disabled={selectedVehicles.length === 0 || isAddingTransportTruck}
          startIcon={isAddingTransportTruck ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isAddingTransportTruck ? 'Adding...' : 'Add Truck'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TruckSelectorDialog
